import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import fileDownload from 'js-file-download';

import styles from '../styles';
import { errorNotificationOnBlob, successNotification } from '../Snackbar';

import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import Breadcrumbs from '../Components/Breadcrumbs';
import { clearDebitNotes, getDebitNotes } from '../Actions';

const DebitNoteList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const debitNotes = useSelector(state => state.debitNotes);

  useEffect(() => {
    dispatch(getDebitNotes());
    return () => {
      dispatch(clearDebitNotes());
    };
  }, []);

  const onFileDownload = useCallback(async id => {
    try {
      const { data } = await Api.getFile(
        `/quality-checks/download/debit-note/${id}`
      );
      fileDownload(data, `debit_note-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  }, []);

  const onChallanFileDownload = useCallback(async id => {
    try {
      const { data } = await Api.getFile(
        `/quality-checks/download/debit-challan/${id}`
      );
      fileDownload(data, `debit_challan-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  }, []);

  const downloadColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="File">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onFileDownload(record.id)}
          />
        </Tooltip>
      ),
    []
  );

  const downloadChallanColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="File">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onChallanFileDownload(record.id)}
          />
        </Tooltip>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'QC #',
        dataIndex: 'qcNo',
        sorter: sorter('qcNo'),
        onFilter: onFilter('qcNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'QC Date',
        dataIndex: 'qcDate',
        sorter: dateSorter('qcDate'),
        onFilter: onDateFilter('qcDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: qcdate =>
          qcdate ? moment(qcdate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO Date',
        dataIndex: 'rpoDate',
        sorter: dateSorter('rpoDate'),
        onFilter: onDateFilter('rpoDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: rpoDate =>
          rpoDate ? moment(rpoDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Debit Note',
        render: downloadColumn,
        fixed: 'right',
        width: '6%',
      },
      {
        title: 'Challan',
        render: downloadChallanColumn,
        fixed: 'right',
        width: '6%',
      },
    ];

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1600, y: 650 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Debit Note"
        moduleLink="/debitnote/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={debitNotes}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

DebitNoteList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(DebitNoteList);
